import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  SkipDialogActions,
  SkipDialogBody,
  SkipDialogDescription,
  SkipDialogPrimary,
  SkipDialogSecondary,
  SkipDialogTitle,
} from '../Onboarding.styled';
import { NarrowDialog } from './SkipDialog.styled';

interface SkipDialogProps {
  show: boolean;
  loading?: boolean;
  /** Called when the user picks "Continue setup" or dismisses the dialog. */
  onContinue: () => void;
  /** Called when the user picks "Skip for now". */
  onSkip: () => void;
}

/**
 * Compact skip-confirmation dialog for the onboarding wizard. Built directly
 * on the `Dialog` molecule to achieve the 28rem width and tighter typography
 * — the shared `ConfirmDialog` renders the title via `NavigationHeader` at
 * 22px and the dialog at 600px which is both too tall and too wide.
 *
 * The X close-button in the top-right is rendered by `Dialog` itself when we
 * pass `onCloseIconButtonClick`. The primary button keeps the user in the
 * setup flow; the secondary button is the actual "skip" action.
 */
const SkipDialog: React.FC<SkipDialogProps> = ({
  show,
  loading,
  onContinue,
  onSkip,
}) => {
  const intl = useIntl();
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (show) primaryRef.current?.focus();
  }, [show]);

  return (
    <NarrowDialog
      show={show}
      onClose={onContinue}
      onCloseIconButtonClick={onContinue}
      closeOnEscape
      closeOnClickOutside
      disableContentPadding
      dataTestId="onboarding-skip-dialog"
    >
      <SkipDialogBody>
        <SkipDialogTitle data-testid="onboarding-skip-dialog__title">
          {intl.formatMessage({ id: 'onboarding.skip.confirm.title' })}
        </SkipDialogTitle>
        <SkipDialogDescription>
          {intl.formatMessage({ id: 'onboarding.skip.confirm.body' })}
        </SkipDialogDescription>

        <SkipDialogActions>
          <SkipDialogSecondary
            type="button"
            onClick={onSkip}
            disabled={loading}
            data-testid="onboarding-skip-dialog__skip"
          >
            {intl.formatMessage({ id: 'onboarding.skip.confirm.confirm' })}
          </SkipDialogSecondary>
          <SkipDialogPrimary
            ref={primaryRef}
            type="button"
            onClick={onContinue}
            disabled={loading}
            data-testid="onboarding-skip-dialog__continue"
          >
            {intl.formatMessage({ id: 'onboarding.skip.confirm.cancel' })}
          </SkipDialogPrimary>
        </SkipDialogActions>
      </SkipDialogBody>
    </NarrowDialog>
  );
};

export default SkipDialog;
